<template>
  <div class="search-modal__wrapper">
    <search-form
      v-if="view !== 'desktop-form'"
      ref="searchForm"
      view="mobile-form"
      @update:query="$emit('update:query', $event)"
    />

    <div
      v-if="overlay.isActive"
      v-show="shouldShowContent"
      class="search-modal grid container"
    >
      <div
        v-show="computedQuery"
        class="col xs4 l1-3 search-modal__group-wrapper"
      >
        <ais-index :index-name="algolia.indexSuggestions">
          <ais-configure :hits-per-page.camel="3" />

          <ais-hits
            :class-names="{
              'ais-Hits': 'search-modal__group',
            }"
          >
            <template #default="{ items }">
              <p
                class="search-modal__group-label text-body-xs"
                v-text="$string('predictive_search.suggestions_label')"
              />

              <ul
                v-if="items.length"
                class="search-modal__results-list"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                >
                  <a
                    :href="getUrl(item, 'search')"
                    class="text-body-xs search-modal__result-link"
                  >
                    <ais-highlight
                      :hit="item"
                      attribute="query"
                      highlighted-tag-name="b"
                      :class-names="{
                        'ais-Highlight': 'text-body-xs',
                      }"
                    />
                  </a>
                </li>
              </ul>
              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>

        <hr
          v-show="view === 'desktop-form'"
          class="search-modal__divider"
        >

        <ais-index
          v-show="view === 'desktop-form'"
          :index-name="algolia.indexCollections"
        >
          <algolia-stats-connector
            @nb-hits-change="handleCollectionsHits"
          />
          <ais-configure :hits-per-page.camel="4" />

          <ais-hits
            :class-names="{
              'ais-Hits': 'search-modal__group',
            }"
          >
            <template #default="{ items }">
              <p
                class="search-modal__group-label text-body-xs"
                v-text="$string('predictive_search.collections_label')"
              />

              <ul
                v-if="items.length"
                class="search-modal__results-list"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                >
                  <a
                    :href="getUrl(item, 'collections')"
                    class="text-body-xs search-modal__result-link"
                  >
                    <ais-highlight
                      :hit="item"
                      attribute="title"
                      highlighted-tag-name="b"
                      :class-names="{
                        'ais-Highlight': 'text-body-xs',
                      }"
                    />
                  </a>
                </li>
              </ul>

              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>

        <hr
          v-show="view === 'desktop-form'"
          class="search-modal__divider"
        >

        <ais-index
          v-show="view === 'desktop-form'"
          :index-name="algolia.indexPages"
        >
          <algolia-stats-connector
            @nb-hits-change="handlePagesHits"
          />
          <ais-configure :hits-per-page.camel="4" />

          <ais-hits
            :class-names="{
              'ais-Hits': 'search-modal__group',
            }"
          >
            <template #default="{ items }">
              <p
                class="search-modal__group-label text-body-xs"
                v-text="$string('predictive_search.pages_label')"
              />

              <ul
                v-if="items.length"
                class="search-modal__results-list"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                >
                  <a
                    :href="getUrl(item, 'pages')"
                    class="text-body-xs search-modal__result-link"
                  >
                    <ais-highlight
                      :hit="item"
                      attribute="title"
                      highlighted-tag-name="b"
                      :class-names="{
                        'ais-Highlight': 'text-body-xs',
                      }"
                    />
                  </a>
                </li>
              </ul>

              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>

        <a
          v-if="!isMobile && overlay.isActive"
          class="search-modal__logo"
          :href="$variable('routes.root')"
          :aria-label="$string('general.site_header.return_home')"
        >
          <icon-logo />
        </a>
      </div>

      <div
        v-show="!computedQuery"
        class="col xs4 l1-3 search-modal__group-wrapper"
      >
        <ais-index :index-name="algolia.indexSuggestions">
          <ais-configure :hits-per-page.camel="6" />

          <ais-hits
            :class-names="{
              'ais-Hits': 'search-modal__group',
            }"
          >
            <template #default="{ items }">
              <p
                class="search-modal__group-label text-body-xs"
                v-text="$string('predictive_search.popular_searches_label')"
              />

              <ul
                v-if="items.length"
                class="search-modal__results-list"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                >
                  <a
                    :href="getUrl(item, 'search')"
                    class="text-body-xs search-modal__result-link"
                  >
                    <ais-highlight
                      :hit="item"
                      attribute="query"
                      highlighted-tag-name="b"
                      :class-names="{
                        'ais-Highlight': 'text-body-xs',
                      }"
                    />
                  </a>
                </li>
              </ul>
              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>

        <a
          v-if="overlay.isActive && !isMobile"
          class="search-modal__logo"
          :href="$variable('routes.root')"
          :aria-label="$string('general.site_header.return_home')"
        >
          <icon-logo />
        </a>
      </div>

      <hr class="search-modal__divider--vertical">

      <div class="col xs4 l5-12 search-modal__products-wrapper">
        <ais-index
          :key="`products-${currentProductsIndex}`"
          :index-name="currentProductsIndex"
        >
          <algolia-stats-connector
            @nb-hits-change="handleProductsHits"
          />
          <ais-configure
            distinct
            :hits-per-page.camel="view === 'desktop-form' ? 3 : 4"
            :faceting-after-distinct.camel="true"
            :filters="algolia.defaultFilters"
          />

          <ais-hits
            :class-names="{
              'ais-Hits': `search-modal__group ${productsCount > 0 ? 'small-gap' : ''}`,
            }"
            :transform-items="transformItems"
          >
            <template #default="{ items }">
              <div class="search-modal__group-header">
                <p
                  v-if="computedQuery"
                  class="search-modal__group-label text-body-xs"
                  v-text="$string('predictive_search.products_label')"
                />
                <p
                  v-else
                  class="search-modal__group-label text-body-xs"
                  v-text="$string('predictive_search.featured_products_label')"
                />

                <a
                  v-if="computedQuery && productsCount > 0"
                  class="search-modal__group-cta text-body-3xs-link"
                  :href="getUrl({ query: computedQuery }, 'search')"
                  v-text="$string('predictive_search.view_results', {
                    replace: {
                      count: productsCount,
                    },
                  })"
                />
              </div>

              <ul
                v-if="items.length"
                class="search-modal__products"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                  class="search-modal__product"
                >
                  <product-card
                    v-if="overlay.isActive"
                    :badge="generateProductBadge(item)"
                    :product="item"
                    :position="item['__position']"
                    section-type="search modal"
                    section-name="search modal"
                  />
                </li>
              </ul>
              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>
      </div>

      <template v-if="view !== 'desktop-form' && computedQuery">
        <hr class="search-modal__divider">

        <ais-index
          :index-name="algolia.indexCollections"
          class="col xs4"
        >
          <algolia-stats-connector
            @nb-hits-change="handleCollectionsHits"
          />
          <ais-configure :hits-per-page.camel="4" />

          <ais-hits
            :class-names="{
              'ais-Hits': 'search-modal__group',
            }"
          >
            <template #default="{ items }">
              <p
                class="search-modal__group-label text-body-xs"
                v-text="$string('predictive_search.collections_label')"
              />

              <ul
                v-if="items.length"
                class="search-modal__results-list"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                >
                  <a
                    :href="getUrl(item, 'collections')"
                    class="text-body-xs search-modal__result-link"
                  >
                    <ais-highlight
                      :hit="item"
                      attribute="title"
                      highlighted-tag-name="b"
                      :class-names="{
                        'ais-Highlight': 'text-body-xs',
                      }"
                    />
                  </a>
                </li>
              </ul>

              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>

        <hr class="search-modal__divider">

        <ais-index
          :index-name="algolia.indexPages"
          class="col xs4"
        >
          <algolia-stats-connector
            @nb-hits-change="handlePagesHits"
          />
          <ais-configure :hits-per-page.camel="4" />

          <ais-hits
            :class-names="{
              'ais-Hits': 'search-modal__group',
            }"
          >
            <template #default="{ items }">
              <p
                class="search-modal__group-label text-body-xs"
                v-text="$string('predictive_search.pages_label')"
              />

              <ul
                v-if="items.length"
                class="search-modal__results-list"
              >
                <li
                  v-for="item of items"
                  :key="item.objectID"
                >
                  <a
                    :href="getUrl(item, 'pages')"
                    class="text-body-xs search-modal__result-link"
                  >
                    <ais-highlight
                      :hit="item"
                      attribute="title"
                      highlighted-tag-name="b"
                      :class-names="{
                        'ais-Highlight': 'text-body-xs',
                      }"
                    />
                  </a>
                </li>
              </ul>

              <p
                v-else
                class="text-body-xs"
                v-text="$string('predictive_search.general_no_results')"
              />
            </template>
          </ais-hits>
        </ais-index>
      </template>
    </div>
  </div>

  <empty-search-state v-if="resultsCount === 0 && hasLoadedResults" />
</template>

<script>

/**
 * Vue: Search overlay (search-modal)
 * -----------------------------------------------------------------------------
 * Predictive search overlay.
 * @param {Object} overlay - Overlay API.
 *
 */
import { mapState } from 'vuex'
import { AisConfigure, AisHits, AisIndex, AisHighlight } from 'vue-instantsearch/vue3/es'
import ProductCard from '~async/product-card/product-card'
import AlgoliaStatsConnector from '~async/algolia/algolia-stats-connector'
import EmptySearchState from '~async/empty-search-state/empty-search-state'
import SearchForm from '~async/search-form/search-form'

import { formatAlgoliaProduct, generateProductBadge } from '~/helpers/format-algolia'
import IconLogo from '~icons/misc/logo.svg'

export default {
  name: 'SearchModal',

  components: {
    AisIndex,
    AisHits,
    AisConfigure,
    AisHighlight,
    AlgoliaStatsConnector,
    ProductCard,
    EmptySearchState,
    SearchForm,
    IconLogo,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
    query: {
      type: String,
      default: '',
    },
    focusElements: {
      type: [null, Array],
      default: () => [],
    },
    view: {
      // 'desktop-form', 'mobile-form' or 'toggle-modal'
      type: String,
      default: 'desktop-form',
    },
  },

  emits: ['update:query'],

  data() {
    return {
      productsCount: -1,
      collectionsCount: 0,
      pagesCount: 0,
      hasLoadedResults: false,
      cachedTrendingProductsCount: null,
      algolia: {
        indexProducts: this.$variable('algolia.indexes.products'),
        indexTrendingProducts: this.$variable('algolia.indexes.trendingProducts'),
        indexCollections: this.$variable('algolia.indexes.collections'),
        indexPages: this.$variable('algolia.indexes.pages'),
        indexSuggestions: this.$variable('algolia.indexes.suggestions'),
        defaultFilters: this.$variable('algolia.defaultFilters'),
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns the total number of results.
     * @returns {Number} - Total search results.
     */
    resultsCount() {
      return this.productsCount + this.collectionsCount + this.pagesCount
    },

    /**
     * Computed query from prop.
     * @returns {String} - Current search query.
     */
    computedQuery() {
      return this.query || ''
    },

    /**
     * Get the current products index name based on query.
     * @returns {String} - Current index name.
     */
    currentProductsIndex() {
      return this.computedQuery ? this.algolia.indexProducts : this.algolia.indexTrendingProducts
    },

    /**
     * Determine if content should be shown.
     * Only show when trending products have been cached (first load complete).
     * @returns {Boolean} - Whether to show the content.
     */
    shouldShowContent() {
      // Don't show until trending products have been cached (first load complete)
      return this.cachedTrendingProductsCount !== null
    },
  },

  watch: {
    async 'overlay.isActive'() {
      if (this.overlay.isActive && this.focusElements) {
        // Ensure query is empty FIRST before resetting counts
        // This ensures the correct index (trending products) is used
        if (this.query) {
          this.$emit('update:query', '')
          // Wait for query to clear before proceeding
          await this.$nextTick()
        }

        // Reset counts and loading state when overlay opens
        this.collectionsCount = 0
        this.pagesCount = 0
        this.hasLoadedResults = false

        // Always use cached trending products count if available (query is always empty on open)
        // This makes the content visible immediately instead of waiting for Algolia to fetch
        if (this.cachedTrendingProductsCount !== null) {
          this.productsCount = this.cachedTrendingProductsCount
          this.hasLoadedResults = true
        } else {
          this.productsCount = -1
        }

        await this.$nextTick()

        this.overlay.update({
          currentElement: document.activeElement,
          focusElements: {
            prepend: true,
            elements: this.focusElements,
          },
        })
      } else if (!this.overlay.isActive) {
        // Reset counts and clear query when overlay closes
        this.productsCount = -1
        this.collectionsCount = 0
        this.pagesCount = 0
        // Emit empty query to clear parent
        this.$emit('update:query', '')
      }
    },

    /**
     * Reset products count when query is cleared.
     */
    computedQuery: {
      handler(newQuery, oldQuery) {
        // Reset when query is cleared (going from having a query to empty)
        if (oldQuery && !newQuery && this.overlay.isActive) {
          this.productsCount = -1
          this.hasLoadedResults = false
        }
      },
      immediate: false,
    },

    /**
     * Mark as loaded when products count changes from loading state.
     */
    productsCount(newCount) {
      if (!this.hasLoadedResults && newCount !== -1) {
        this.hasLoadedResults = true
      }
    },

  },


  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
  },

  methods: {

    /**
     * Close overlay on escape key press.
     * @param {Event} event - Keydown event.
     */
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.overlay.close()
      }
    },

    /**
     * Transform items to properly pass props to the product card component.
     * @param {Array} items - Algolia items.
     * @returns {Array}
     */
    transformItems(items) {
      if (!items || items.length === 0) {
        return []
      }
      return items.map((item) => formatAlgoliaProduct(item))
    },

    /**
     * Set number of hits per algolia index.
     * @param {Number} nbHits - number of hits/results.
     * @param {String} algoliaIndex - Algolia index name.
     */
    nbHitsChange(nbHits, algoliaIndex) {
      switch (algoliaIndex) {
        case this.algolia.indexProducts:
          // Regular products search
          this.productsCount = nbHits
          // Mark as loaded once we receive product results
          if (this.productsCount !== -1) {
            this.hasLoadedResults = true
          }
          break
        case this.algolia.indexTrendingProducts:
          // Trending products - cache the count for future use
          this.productsCount = nbHits
          this.cachedTrendingProductsCount = nbHits
          // Mark as loaded once we receive product results
          if (this.productsCount !== -1) {
            this.hasLoadedResults = true
          }
          break
        case this.algolia.indexCollections:
          this.collectionsCount = nbHits
          break
        case this.algolia.indexPages:
          this.pagesCount = nbHits
          break
      }
    },

    /**
     * Handle collections hits change
     * @param {Number} nbHits - number of hits/results.
     */
    handleCollectionsHits(nbHits) {
      this.nbHitsChange(nbHits, this.algolia.indexCollections)
    },

    /**
     * Handle pages hits change
     * @param {Number} nbHits - number of hits/results.
     */
    handlePagesHits(nbHits) {
      this.nbHitsChange(nbHits, this.algolia.indexPages)
    },

    /**
     * Handle products hits change (works for both products and trending products)
     * @param {Number} nbHits - number of hits/results.
     */
    handleProductsHits(nbHits) {
      // Use the computed current index
      this.nbHitsChange(nbHits, this.currentProductsIndex)
    },

    /**
     * Generates a badge object from the given product.
     * @param {Object} - Product object.
     * @returns {Object}
     */
    generateProductBadge(product) {
      return generateProductBadge(product)
    },

    /**
     * Get url for search page including Root route.
     */
    getUrl(record, type) {
      switch (type) {
        case 'search':
          return `${this.$variable('routes.search')}?query=${record.query}`
        case 'collections':
          return `${this.$variable('routes.root')}collections/${record.handle}`
        case 'pages':
          return `${this.$variable('routes.root')}pages/${record.handle}`
        case 'popular':
          return `${this.$variable('routes.root')}search?query=${record}`
        default:
          return ''
      }
    },
  },
}
</script>

<style lang="scss">
@import './search-modal';
</style>
